/**
 * Unified API Service for Muraasala CRM
 *
 * All requests go through cms-backend (REACT_APP_CMS_API_URL).
 * WhatsApp operations are proxied via /api/whatsapp/* on cms-backend,
 * which forwards them to api-backend (be.muraasala.com in production).
 * External service-user integrations talk directly to be.muraasala.com.
 */

import axios from 'axios';

const CMS_URL = process.env.REACT_APP_CMS_API_URL ?? '';

export const getAuthToken = () => {
  return localStorage.getItem('token') || '';
};

export const getUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

/**
 * Single axios client — all requests go to cms-backend.
 * cms-backend proxies WhatsApp operations to api-backend internally.
 */
const cmsClient = axios.create({
  baseURL: CMS_URL,
  headers: {'Content-Type': 'application/json'},
  timeout: 60000,
});

cmsClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;

    // Forward API key so cms-backend whatsapp proxy can authenticate to api-backend
    const user = getUser();
    if (user && user.api_key) {
      config.headers['x-whatsapp-api-key'] = user.api_key;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Normalize an axios rejection into a real Error.
 *
 * This interceptor used to reject `error.response.data` — a plain object with
 * no `.message` and no `.response`. Call sites across the portal read either
 * `err.message` or `err?.response?.data?.error`, so BOTH were undefined and
 * every dialog rendered a blank error. Prod symptom: a 429 body of
 * {error: 'Throttled: WARMUP_CAP', code, retryAfterMs} was visible in the
 * network tab while the snackbar showed nothing.
 *
 * Fixing it here rather than at the call sites keeps all three access patterns
 * working: `.message` (now populated), the payload fields (`.error`, `.code`,
 * `.retryAfterMs`, preserved), and `.response.data.error` (shim kept).
 */
export function normalizeApiError(error) {
  const data = error?.response?.data;
  let message;

  if (data && typeof data === 'object') {
    message = data.error || data.message;
  } else if (
    typeof data === 'string' &&
    data.trim() &&
    !data.trim().startsWith('<')
  ) {
    // Plain-text body is useful; an HTML error page is not - fall through to status.
    message = data.trim().slice(0, 200);
  }

  if (!message) message = error?.message;
  if (!message) {
    const status = error?.response?.status;
    message = status ? `Request failed (HTTP ${status})` : 'Request failed';
  }

  const normalized = new Error(message);
  // Carry the payload so existing `err.error` / `err.code` reads keep working.
  if (data && typeof data === 'object') Object.assign(normalized, data);
  // Re-assert: a payload key named `message` must not clobber the chosen text.
  normalized.message = message;
  if (error?.response) normalized.response = error.response;
  if (error?.code && !normalized.code) normalized.code = error.code;
  return normalized;
}

cmsClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      console.warn('[CmsAPI] Auth failed -> Force logout');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Clearing the token is not enough on its own: the tab stays on the page
      // it was on, still rendering the authenticated shell, while every request
      // 401s. The user sees a screen that looks signed in and silently does
      // nothing, with no hint that they need to sign in again.
      //
      // GlobalAuthListener in App.js listens for this and navigates to /signin.
      // services/cms/api.js and @crema/services/auth/jwt-auth both dispatch it;
      // this client was the one that did not.
      //
      // 401 ONLY. 403 means "signed in and not allowed" — what the portal RBAC
      // gate returns — and signing someone out for opening a page they lack
      // permission for would be a worse bug than the one this fixes.
      window.dispatchEvent(new CustomEvent('unauthorized'));
    }
    return Promise.reject(normalizeApiError(error));
  },
);

class UnifiedApiService {
  // ==================== AUTHENTICATION ====================

  async login(credentials) {
    const response = await cmsClient.post('/api/auth/login', credentials);
    if (response.token) localStorage.setItem('token', response.token);
    if (response.user)
      localStorage.setItem('user', JSON.stringify(response.user));
    return response;
  }

  async logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return {success: true};
  }

  async getCurrentUser() {
    return cmsClient.get('/api/auth/me');
  }

  async forgotPassword(email) {
    return cmsClient.post('/api/auth/forgot-password', {email});
  }

  async resetPassword(token, password) {
    return cmsClient.post('/api/auth/reset-password', {token, password});
  }

  // ==================== USER MANAGEMENT ====================

  async getUsers(params = {}) {
    return cmsClient.get('/api/users', {params});
  }

  async getUserActivity() {
    return cmsClient.get('/api/users/activity');
  }

  async getUserById(userId) {
    return cmsClient.get(`/api/users/${userId}`);
  }

  async createUser(userData) {
    return cmsClient.post('/api/users', userData);
  }

  async updateUser(userId, userData) {
    return cmsClient.put(`/api/users/${userId}`, userData);
  }

  async deleteUser(userId) {
    return cmsClient.delete(`/api/users/${userId}`);
  }

  async toggleUserStatus(userId) {
    return cmsClient.post(`/api/users/${userId}/toggle-status`);
  }

  async getProfile() {
    return this.getCurrentUser();
  }

  // ==================== SERVICE USER MANAGEMENT (via cms-backend proxy) ====================

  async createServiceUser(data) {
    return cmsClient.post('/admin/users', data);
  }

  async getServiceUsers(params = {}) {
    return cmsClient.get('/api/users', {params});
  }

  async updateServiceUser(userId, data) {
    return cmsClient.put(`/api/users/${userId}`, data);
  }

  async getUserDetails(userId) {
    return cmsClient.get(`/admin/users/${userId}`);
  }

  async getUserFullDetails(userId) {
    return cmsClient.get(`/admin/users/${userId}/details`);
  }

  async getCmsUsers(params) {
    if (params && params.isServiceUser === true) {
      // Forward the params. This used to call '/admin/users' bare, dropping page,
      // limit and search entirely — so the Service Users screen took the server's
      // default (a 1,000-row cap) and did all search, filter and pagination in
      // the browser. A tenant past that cap saw 1,000 users and no indication
      // there were more. admin.js:211-213 has honoured page/limit all along.
      // isServiceUser is this method's own routing flag, not a query param —
      // strip it rather than forwarding it upstream.
      const rest = {...params};
      delete rest.isServiceUser;
      return cmsClient.get('/admin/users', {params: rest});
    }
    return this.getUsers(params);
  }

  // ==================== POLLS ====================
  //
  // Routed through the cms /api/whatsapp/* catch-all proxy like every other
  // service call, so the caller's key is resolved server-side and never
  // reaches the browser.

  async getPolls(params) {
    return cmsClient.get('/api/whatsapp/polls', {params});
  }

  async getPoll(pollId) {
    return cmsClient.get(`/api/whatsapp/polls/${pollId}`);
  }

  async createPoll(data) {
    return cmsClient.post('/api/whatsapp/polls', data);
  }

  async resendPoll(pollId) {
    return cmsClient.post(`/api/polls/${pollId}/resend`);
  }

  async createPollForm(data) {
    return cmsClient.post('/api/polls/forms', data);
  }

  async getPollForms(params) {
    return cmsClient.get('/api/polls/forms', {params});
  }

  async getPollForm(formId) {
    return cmsClient.get(`/api/polls/forms/${formId}`);
  }

  async cancelPoll(pollId) {
    return cmsClient.post(`/api/whatsapp/polls/${pollId}/cancel`);
  }

  // ==================== BALANCE MANAGEMENT ====================

  async getMyBalance() {
    return cmsClient.get('/api/balance/me');
  }

  async getUserBalance(userId) {
    return cmsClient.get(`/api/balance/${userId}`);
  }

  async rechargeUser(userId, amount, notes = '') {
    return cmsClient.post(`/api/balance/${userId}/recharge`, {amount, notes});
  }

  async rechargeUserBalance(userId, data) {
    return cmsClient.post(`/api/balance/${userId}/recharge`, data);
  }

  async getLedger(userId, params = {}) {
    return cmsClient.get(`/api/balance/${userId}/history`, {params});
  }

  async getBalanceMonitor() {
    return cmsClient.get('/api/balance/monitor');
  }

  // ==================== WHATSAPP ACCOUNTS (via cms-backend /api/whatsapp proxy) ====================

  async getAccounts() {
    return cmsClient.get('/api/whatsapp/accounts');
  }

  async getConnectionStatus() {
    return cmsClient.get('/api/whatsapp/accounts');
  }

  async connectAccount(accountType = 'primary') {
    return cmsClient.get('/api/whatsapp/accounts/connect', {
      params: {accountType},
    });
  }

  async disconnectAccount(accountId) {
    return cmsClient.post(`/api/whatsapp/accounts/${accountId}/disconnect`);
  }

  async checkWhatsApp(phone) {
    return cmsClient.post('/api/whatsapp/accounts/check-whatsapp', {phone});
  }

  async getAccountQR(accountType = 'primary') {
    return cmsClient.get('/api/whatsapp/accounts/connect', {
      params: {accountType},
    });
  }

  // ============ ACCOUNT OPERATIONS — block/unblock + WhatsApp profile (backend #45) ============
  // Proxy to api-backend /api/accounts/:id/*. accountId is an internal id (safe
  // chars), unencoded to match disconnectAccount().

  async setBlockStatus(accountId, phone, action) {
    return cmsClient.post(`/api/whatsapp/accounts/${accountId}/block`, {
      phone,
      action,
    });
  }

  async getBlocklist(accountId) {
    return cmsClient.get(`/api/whatsapp/accounts/${accountId}/blocklist`);
  }

  async updateAccountProfileName(accountId, name) {
    return cmsClient.put(`/api/whatsapp/accounts/${accountId}/profile/name`, {
      name,
    });
  }

  async updateAccountProfileStatus(accountId, status) {
    return cmsClient.put(`/api/whatsapp/accounts/${accountId}/profile/status`, {
      status,
    });
  }

  async updateAccountProfilePicture(accountId, imageBase64) {
    // The engine does Buffer.from(imageBase64, 'base64'), so send RAW base64 —
    // strip any data-URL prefix a FileReader.readAsDataURL() produced.
    const raw = String(imageBase64 || '').replace(
      /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
      '',
    );
    return cmsClient.put(
      `/api/whatsapp/accounts/${accountId}/profile/picture`,
      {imageBase64: raw},
    );
  }

  async removeAccountProfilePicture(accountId) {
    return cmsClient.post(
      `/api/whatsapp/accounts/${accountId}/profile/picture/remove`,
    );
  }

  // ============ PRIVACY SETTINGS (engine exposure, account-scoped by id) ============

  async getAccountPrivacy(accountId) {
    return cmsClient.get(`/api/whatsapp/accounts/${accountId}/privacy`);
  }

  async updateAccountPrivacy(accountId, setting, value) {
    return cmsClient.put(`/api/whatsapp/accounts/${accountId}/privacy`, {
      setting,
      value,
    });
  }

  async setAccountDisappearing(accountId, seconds) {
    return cmsClient.put(
      `/api/whatsapp/accounts/${accountId}/privacy/disappearing`,
      {seconds},
    );
  }

  // Request an 8-char phone-number pairing code (QR alternative) for an
  // initialised account. Backend enforces tenant ownership of accountId.
  async requestPairingCode(accountId, phoneNumber) {
    return cmsClient.post(`/api/whatsapp/accounts/${accountId}/pairing-code`, {
      phoneNumber,
    });
  }

  // ============ COMMUNITIES (via cms-backend /api/whatsapp proxy) ============
  // Backed by api-backend /api/communities (tenant-scoped, RBAC service.communities).
  // Community JIDs are `<id>@g.us` → URL-encoded.

  async getCommunities() {
    return cmsClient.get('/api/whatsapp/communities');
  }

  async getCommunityMetadata(jid) {
    return cmsClient.get(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}`,
    );
  }

  async getCommunityLinkedGroups(jid) {
    return cmsClient.get(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/linked-groups`,
    );
  }

  async getCommunityRequests(jid) {
    return cmsClient.get(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/requests`,
    );
  }

  async getCommunityInviteCode(jid) {
    return cmsClient.get(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/invite`,
    );
  }

  async getCommunityInviteInfo(code) {
    return cmsClient.get('/api/whatsapp/communities/invite-info', {
      params: {code},
    });
  }

  async createCommunity(data) {
    return cmsClient.post('/api/whatsapp/communities', data);
  }

  async acceptCommunityInvite(code) {
    return cmsClient.post('/api/whatsapp/communities/accept-invite', {code});
  }

  async createCommunityGroup(jid, data) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/groups`,
      data,
    );
  }

  async linkCommunityGroup(jid, groupJid) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/link`,
      {groupJid},
    );
  }

  async unlinkCommunityGroup(jid, groupJid) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/unlink`,
      {groupJid},
    );
  }

  async updateCommunityParticipants(jid, participants, action) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/participants`,
      {participants, action},
    );
  }

  async updateCommunityRequests(jid, participants, approve) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/requests`,
      {participants, approve},
    );
  }

  async revokeCommunityInvite(jid) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/invite/revoke`,
    );
  }

  async leaveCommunity(jid) {
    return cmsClient.post(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/leave`,
    );
  }

  async updateCommunitySubject(jid, subject) {
    return cmsClient.put(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/subject`,
      {subject},
    );
  }

  async updateCommunityDescription(jid, description) {
    return cmsClient.put(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/description`,
      {description},
    );
  }

  async toggleCommunityEphemeral(jid, seconds) {
    return cmsClient.put(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/ephemeral`,
      {seconds},
    );
  }

  async setCommunityMemberAddMode(jid, mode) {
    return cmsClient.put(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/member-add-mode`,
      {mode},
    );
  }

  async setCommunityJoinApprovalMode(jid, mode) {
    return cmsClient.put(
      `/api/whatsapp/communities/${encodeURIComponent(jid)}/join-approval-mode`,
      {mode},
    );
  }

  // ======== BUSINESS: CATALOG / PRODUCTS / LABELS (via /api/whatsapp proxy) ========
  // Backed by api-backend /api/business (tenant-scoped, RBAC service.business).

  async getCatalog(params = {}) {
    return cmsClient.get('/api/whatsapp/business/catalog', {params});
  }

  async getCollections(params = {}) {
    return cmsClient.get('/api/whatsapp/business/catalog/collections', {
      params,
    });
  }

  async getOrderDetails(orderId, token) {
    return cmsClient.get(
      `/api/whatsapp/business/orders/${encodeURIComponent(orderId)}`,
      {params: {token}},
    );
  }

  async createProduct(product) {
    return cmsClient.post('/api/whatsapp/business/products', {product});
  }

  async updateProduct(productId, product) {
    return cmsClient.put(
      `/api/whatsapp/business/products/${encodeURIComponent(productId)}`,
      {product},
    );
  }

  async deleteProducts(productIds) {
    return cmsClient.delete('/api/whatsapp/business/products', {
      data: {productIds},
    });
  }

  async addLabel(jid, label) {
    return cmsClient.post('/api/whatsapp/business/labels', {jid, label});
  }

  async attachChatLabel(jid, labelId) {
    return cmsClient.post('/api/whatsapp/business/labels/attach', {
      jid,
      labelId,
    });
  }

  async detachChatLabel(jid, labelId) {
    return cmsClient.post('/api/whatsapp/business/labels/detach', {
      jid,
      labelId,
    });
  }

  // ============ CALLS (via cms-backend /api/whatsapp proxy) ============
  // Backed by api-backend /api/calls (tenant-scoped, RBAC service.calls). History
  // is DB-backed (persisted call-events); link/reject proxy to the engine.
  async getCalls(params = {}) {
    return cmsClient.get('/api/whatsapp/calls', {params});
  }

  async getCallHistory(params = {}) {
    return cmsClient.get('/api/whatsapp/calls/history', {params});
  }

  async createCallLink(data = {}) {
    return cmsClient.post('/api/whatsapp/calls/link', data);
  }

  async rejectCall(callId, callFrom) {
    return cmsClient.post(
      `/api/whatsapp/calls/${encodeURIComponent(callId)}/reject`,
      {callFrom},
    );
  }

  // ============ ANALYTICS (via cms-backend /api/whatsapp proxy) ============
  // Backed by api-backend /api/analytics (tenant-scoped, RBAC service.analytics).
  // All figures are computed from real tenant data (messages / incoming / campaigns).
  async getConversionAnalytics(params = {}) {
    return cmsClient.get('/api/whatsapp/analytics/conversions', {params});
  }

  async getConversationAnalytics(params = {}) {
    return cmsClient.get('/api/whatsapp/analytics/conversations', {params});
  }

  async getMessageAnalytics(params = {}) {
    return cmsClient.get('/api/whatsapp/analytics/messages', {params});
  }

  // ============ NEWSLETTERS / CHANNELS (via cms-backend /api/whatsapp proxy) ============
  // Backed by api-backend /api/newsletters (tenant-scoped, RBAC service.newsletters).
  // JIDs contain '@' → URL-encoded.

  // List the tenant's tracked channels (DB-backed; baileys has no bulk list).
  async getNewsletters() {
    return cmsClient.get('/api/whatsapp/newsletters');
  }

  async getNewsletterMetadata(key, type = 'jid') {
    return cmsClient.get('/api/whatsapp/newsletters/metadata', {
      params: {key, type},
    });
  }

  async getNewsletterSubscribers(jid) {
    return cmsClient.get(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/subscribers`,
    );
  }

  async getNewsletterAdminCount(jid) {
    return cmsClient.get(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/admin-count`,
    );
  }

  async getNewsletterMessages(jid, params = {}) {
    return cmsClient.get(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/messages`,
      {params},
    );
  }

  async createNewsletter(data) {
    return cmsClient.post('/api/whatsapp/newsletters', data);
  }

  async updateNewsletter(jid, updates) {
    return cmsClient.patch(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}`,
      {updates},
    );
  }

  async updateNewsletterName(jid, name) {
    return cmsClient.put(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/name`,
      {name},
    );
  }

  async updateNewsletterDescription(jid, description) {
    return cmsClient.put(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/description`,
      {description},
    );
  }

  async updateNewsletterPicture(jid, imageBase64) {
    return cmsClient.put(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/picture`,
      {imageBase64},
    );
  }

  async removeNewsletterPicture(jid) {
    return cmsClient.delete(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/picture`,
    );
  }

  async followNewsletter(jid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/follow`,
    );
  }

  async unfollowNewsletter(jid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/unfollow`,
    );
  }

  async muteNewsletter(jid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/mute`,
    );
  }

  async unmuteNewsletter(jid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/unmute`,
    );
  }

  async changeNewsletterOwner(jid, newOwnerJid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/owner`,
      {newOwnerJid},
    );
  }

  async demoteNewsletterUser(jid, userJid) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}/demote`,
      {userJid},
    );
  }

  async reactToNewsletterMessage(jid, serverId, reaction) {
    return cmsClient.post(
      `/api/whatsapp/newsletters/${encodeURIComponent(
        jid,
      )}/messages/${encodeURIComponent(serverId)}/react`,
      {reaction},
    );
  }

  async deleteNewsletter(jid) {
    return cmsClient.delete(
      `/api/whatsapp/newsletters/${encodeURIComponent(jid)}`,
    );
  }

  // ==================== MESSAGING (via cms-backend /api/whatsapp proxy) ====================

  async sendMessage(messageData) {
    const formData = new FormData();
    for (const key in messageData) {
      formData.append(key, messageData[key]);
    }
    return cmsClient.post('/api/whatsapp/messages/send', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async sendDirectMessageToPhone(phone, message) {
    const formData = new FormData();
    formData.append('phone', phone);
    formData.append('message', message);
    return cmsClient.post('/api/whatsapp/messages/send', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async sendMedia(formData) {
    return cmsClient.post('/api/whatsapp/messages/send-media', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async sendBulkMessagesForm(formData) {
    // Do NOT override Content-Type — let axios/browser generate it with the multipart boundary
    return cmsClient.post('/api/whatsapp/messages/bulk-send', formData);
  }

  async sendMultipleMessages(formData) {
    return this.sendBulkMessagesForm(formData);
  }

  async getMessageHistory(params = {}) {
    return cmsClient.get('/api/whatsapp/messages/history', {params});
  }

  async getRecentMessages(limit = 10) {
    return cmsClient.get('/api/whatsapp/messages/history', {
      params: {limit},
    });
  }

  async getMessageStats(params = {}) {
    return cmsClient.get('/api/messages/stats', {params});
  }

  // ============ RICH MESSAGE SENDS — poll/location/contact/forward (backend #47) ============
  // MONEY path: the api-backend deducts/refunds credits; the frontend only
  // shapes the request. JSON bodies keep array fields (values/contacts/mentions)
  // as real arrays. Recipient is `phone` (backend resolves phoneNumber||phone||to).

  async sendPoll(phone, name, values, options = {}) {
    return cmsClient.post('/api/whatsapp/messages/send-poll', {
      phone,
      name,
      values,
      ...options,
    });
  }

  async sendLocation(phone, latitude, longitude, options = {}) {
    return cmsClient.post('/api/whatsapp/messages/send-location', {
      phone,
      latitude,
      longitude,
      ...options,
    });
  }

  async sendContact(phone, contacts, options = {}) {
    return cmsClient.post('/api/whatsapp/messages/send-contact', {
      phone,
      contacts,
      ...options,
    });
  }

  async forwardMessage(phone, messageId) {
    return cmsClient.post('/api/whatsapp/messages/forward', {phone, messageId});
  }

  // ==================== GROUPS (via cms-backend /api/whatsapp proxy) ====================

  async getGroups() {
    return cmsClient.get('/api/whatsapp/groups');
  }

  async findGroup(name) {
    return cmsClient.get('/api/whatsapp/groups/find', {params: {name}});
  }

  async sendGroupMessage(data) {
    const formData = new FormData();
    for (const key in data) {
      formData.append(key, data[key]);
    }
    return cmsClient.post('/api/whatsapp/groups/send', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async sendGroupMedia(formData) {
    return cmsClient.post('/api/whatsapp/groups/send-media', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async getGroupMembers(groupId) {
    return cmsClient.get(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/members`,
    );
  }

  async importGroupToContacts(groupId, data) {
    return cmsClient.post(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/import-contacts`,
      data,
    );
  }

  // ============ GROUP MANAGEMENT (live engine ops, via cms-backend proxy) ============
  // These proxy to api-backend /api/groups/*. Bodies are sent as JSON so array
  // fields (participants) arrive as real arrays — the api-backend handlers
  // destructure req.body directly and express.json() populates it before the
  // route's upload.none() (which only touches multipart) runs.

  async createGroup(subject, participants = []) {
    return cmsClient.post('/api/whatsapp/groups/create', {
      subject,
      participants,
    });
  }

  async updateGroupParticipants(groupId, participants, action) {
    return cmsClient.post(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/participants`,
      {participants, action},
    );
  }

  async updateGroupSubject(groupId, subject) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/subject`,
      {subject},
    );
  }

  async updateGroupDescription(groupId, description) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/description`,
      {description},
    );
  }

  async getGroupInviteCode(groupId) {
    return cmsClient.get(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/invite-code`,
    );
  }

  async revokeGroupInvite(groupId) {
    return cmsClient.post(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/invite-code/revoke`,
    );
  }

  async getGroupInviteInfo(code) {
    return cmsClient.get('/api/whatsapp/groups/invite-info', {params: {code}});
  }

  async acceptGroupInvite(code) {
    return cmsClient.post('/api/whatsapp/groups/accept-invite', {code});
  }

  async updateGroupSetting(groupId, setting) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/setting`,
      {setting},
    );
  }

  async toggleGroupEphemeral(groupId, seconds) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/ephemeral`,
      {seconds},
    );
  }

  async leaveGroup(groupId) {
    return cmsClient.post(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/leave`,
    );
  }

  // ======== GROUP JOIN-REQUEST / APPROVAL MANAGEMENT (engine exposure) ========

  async getGroupJoinRequests(groupId) {
    return cmsClient.get(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/join-requests`,
    );
  }

  async updateGroupJoinRequests(groupId, participants, action) {
    return cmsClient.post(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/join-requests`,
      {participants, action},
    );
  }

  async setGroupJoinApprovalMode(groupId, mode) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/join-approval-mode`,
      {mode},
    );
  }

  async setGroupMemberAddMode(groupId, mode) {
    return cmsClient.put(
      `/api/whatsapp/groups/${encodeURIComponent(groupId)}/member-add-mode`,
      {mode},
    );
  }

  // ============ WHATSAPP CONTACTS (engine exposure, via cms-backend proxy) ============

  async updateWaContact(phone, name) {
    return cmsClient.post('/api/whatsapp/contacts/wa-name', {phone, name});
  }

  async removeWaContact(phone) {
    return cmsClient.post('/api/whatsapp/contacts/wa-remove', {phone});
  }

  // ==================== PLANS ====================

  async getPlans() {
    return cmsClient.get('/api/plans');
  }

  async createPlan(data) {
    return cmsClient.post('/api/plans', data);
  }

  async updatePlan(id, data) {
    return cmsClient.put(`/api/plans/${id}`, data);
  }

  async deletePlan(id) {
    return cmsClient.delete(`/api/plans/${id}`);
  }

  async getPlanStats() {
    return {data: {total: 0, active: 0}};
  }

  async assignPlan(planId, serviceUserId, notes = '') {
    return cmsClient.post(`/api/plans/${planId}/assign`, {
      serviceUserId,
      notes,
    });
  }

  // ==================== DASHBOARD ====================

  async getDashboardStats() {
    return cmsClient.get('/api/dashboard/stats');
  }

  async getCmsDashboardStats() {
    return this.getDashboardStats();
  }

  async getStats() {
    return this.getDashboardStats();
  }

  async getDailyStats(params = {}) {
    return cmsClient.get('/api/dashboard/stats', {params});
  }

  async getSystemStats() {
    return this.getDashboardStats();
  }

  async getProviderDashboard(providerId) {
    return cmsClient.get(`/api/dashboard/provider/${providerId}`);
  }

  // ==================== SMTP SETTINGS ====================

  async getSmtpSettings() {
    return cmsClient.get('/api/settings/smtp');
  }

  async updateSmtpSettings(data) {
    return cmsClient.put('/api/settings/smtp', data);
  }

  async testSmtpSettings(data) {
    return cmsClient.post('/api/settings/smtp/test', data);
  }

  async getOwnerSettings() {
    return cmsClient.get('/api/settings/owner');
  }

  async updateOwnerSettings(data) {
    return cmsClient.put('/api/settings/owner', data);
  }

  // ==================== API KEYS ====================

  async createApiKey(data) {
    return cmsClient.post('/api/whatsapp/admin/api-keys', data);
  }

  async revokeApiKey(keyId) {
    return cmsClient.delete(`/api/whatsapp/admin/api-keys/${keyId}`);
  }

  // ==================== PORTAL USERS ====================

  async getPortalUsers() {
    return cmsClient.get('/api/portal-users');
  }

  async createPortalUser(data) {
    return cmsClient.post('/api/portal-users', data);
  }

  async getPortalUser(id) {
    return cmsClient.get(`/api/portal-users/${id}`);
  }

  async updatePortalUser(id, data) {
    return cmsClient.put(`/api/portal-users/${id}`, data);
  }

  async setPortalUserPermissions(id, permissions) {
    return cmsClient.put(`/api/portal-users/${id}/permissions`, {permissions});
  }

  async togglePortalUserStatus(id) {
    return cmsClient.put(`/api/portal-users/${id}/toggle-status`);
  }

  async deletePortalUser(id) {
    return cmsClient.delete(`/api/portal-users/${id}`);
  }

  // Account-user "team management" was superseded by Portal Users (the user
  // dashboard's "Account Users" action now routes to /portal-users). The old
  // /api/account-users* endpoints never existed on the backend (404), and the
  // only caller — pages/muraasala/AccountUsers — was orphaned (unrouted,
  // unimported). Both were removed to kill the dead 404-calling code path.

  // ==================== WEBHOOKS ====================

  async getWebhooks() {
    return cmsClient.get('/api/webhooks');
  }

  async createWebhook(data) {
    return cmsClient.post('/api/webhooks', data);
  }

  async updateWebhook(id, data) {
    return cmsClient.put(`/api/webhooks/${id}`, data);
  }

  async deleteWebhook(id) {
    return cmsClient.delete(`/api/webhooks/${id}`);
  }

  async testWebhook(id) {
    return cmsClient.post(`/api/webhooks/${id}/test`);
  }

  async toggleWebhook(id) {
    return cmsClient.post(`/api/webhooks/${id}/toggle`);
  }

  // ==================== NOTIFICATIONS ====================

  async sendBalanceNotification(userId, type, language = 'en', amount = null) {
    return cmsClient.post('/api/notifications/balance', {
      userId,
      type,
      language,
      ...(amount !== null ? {amount} : {}),
    });
  }

  async sendBulkBalanceNotification(type = 'all', language = 'en') {
    return cmsClient.post('/api/notifications/balance/bulk', {type, language});
  }

  // ==================== FINANCIAL & EXPENSES ====================

  async getFinancialSummary(params = {}) {
    return cmsClient.get('/api/financial/summary', {params});
  }

  async getFinancialRevenue(params = {}) {
    return cmsClient.get('/api/financial/revenue', {params});
  }

  async getExpenses(params = {}) {
    return cmsClient.get('/api/expenses', {params});
  }

  async getSystemLogs(params = {}) {
    return cmsClient.get('/api/system/logs', {params});
  }

  async createExpense(data) {
    return cmsClient.post('/api/expenses', data);
  }

  async updateExpense(id, data) {
    return cmsClient.put(`/api/expenses/${id}`, data);
  }

  async deleteExpense(id) {
    return cmsClient.delete(`/api/expenses/${id}`);
  }

  async downloadExpensesReport(params = {}) {
    const {lang = 'en', startDate, endDate, ...restParams} = params;
    const blobData = await cmsClient.get('/api/expenses/report.pdf', {
      params: {lang, startDate, endDate, ...restParams},
      responseType: 'blob',
    });
    const filename = `expenses-report-${lang}${
      startDate ? `-${startDate}` : ''
    }${endDate ? `-${endDate}` : ''}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  async downloadFinancialReport(params = {}) {
    const {lang = 'en', startDate, endDate, ...restParams} = params;
    const blobData = await cmsClient.get('/api/financial/report.pdf', {
      params: {lang, startDate, endDate, ...restParams},
      responseType: 'blob',
    });
    const filename = `financial-report-${lang}${
      startDate ? `-${startDate}` : ''
    }${endDate ? `-${endDate}` : ''}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  // ==================== AGREEMENTS ====================

  async getPendingAgreements(params = {}) {
    return cmsClient.get('/api/agreements/pending', {params});
  }

  async getUserAgreement(userId) {
    return cmsClient.get(`/api/agreements/${userId}`);
  }

  async viewSignedAgreement(userId) {
    const blobData = await cmsClient.get(`/api/agreements/${userId}/file`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(blobData);
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async downloadAgreementPdf(userId, lang = 'bilingual') {
    const blobData = await cmsClient.get(`/api/agreements/${userId}/pdf`, {
      params: {lang},
      responseType: 'blob',
    });
    const langSuffix = lang !== 'bilingual' ? `-${lang}` : '';
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `agreement-${userId}${langSuffix}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  async uploadAgreement(userId, formData) {
    return cmsClient.post(`/api/agreements/${userId}/upload`, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }

  async approveAgreement(userId, notes = '') {
    return cmsClient.post(`/api/agreements/${userId}/approve`, {notes});
  }

  async rejectAgreement(userId, reason) {
    return cmsClient.post(`/api/agreements/${userId}/reject`, {reason});
  }

  async requestAgreementReupload(userId) {
    return cmsClient.post(`/api/agreements/${userId}/request-reupload`);
  }

  // ==================== USER REPORTS ====================

  async downloadUserReport(data = {}) {
    const blobData = await cmsClient.post(
      '/api/reports/user-dashboard.pdf',
      data,
      {
        responseType: 'blob',
      },
    );
    const {lang = 'en'} = data;
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `user-report-${lang}-${dateStr}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  async downloadPendingApprovalsReport(params = {}) {
    const {lang = 'en', ...restParams} = params;
    const blobData = await cmsClient.get('/api/reports/pending-approvals.pdf', {
      params: {lang, ...restParams},
      responseType: 'blob',
    });
    const filename = `pending-approvals-report-${lang}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  async downloadPlatformOverviewReport(params = {}) {
    const {lang = 'en', ...restParams} = params;
    const blobData = await cmsClient.get('/api/reports/platform-overview.pdf', {
      params: {lang, ...restParams},
      responseType: 'blob',
    });
    const filename = `platform-overview-report-${lang}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  async downloadServiceReports(data = {}) {
    const blobData = await cmsClient.post(
      '/api/reports/service-reports.pdf',
      data,
      {
        responseType: 'blob',
      },
    );
    const {lang = 'en'} = data;
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `service-reports-${lang}-${dateStr}.pdf`;
    const url = window.URL.createObjectURL(
      new Blob([blobData], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }
  // ==================== CONTACTS ====================

  async getContacts(params = {}) {
    return cmsClient.get('/api/contacts', {params});
  }

  async getAllContactIds(params = {}) {
    return cmsClient.get('/api/contacts/all-ids', {params});
  }

  async createContact(data) {
    return cmsClient.post('/api/contacts', data);
  }

  async getContact(id) {
    return cmsClient.get(`/api/contacts/${id}`);
  }

  async updateContact(id, data) {
    return cmsClient.put(`/api/contacts/${id}`, data);
  }

  async deleteContact(id) {
    return cmsClient.delete(`/api/contacts/${id}`);
  }

  async bulkDeleteContacts(ids) {
    return cmsClient.delete('/api/contacts/bulk', {data: {ids}});
  }

  async checkPhone(phone) {
    return cmsClient.post('/api/contacts/check-phone', {phone});
  }

  async refreshContactWhatsApp(id) {
    return cmsClient.post(`/api/contacts/${id}/refresh-whatsapp`);
  }

  /**
   * Bulk-refresh WhatsApp-derived fields (whatsapp_name, avatar_url,
   * is_whatsapp_user) for a set of contacts.
   *
   * @param {object} payload
   * @param {string[]} [payload.ids]      Refresh only these contact ids.
   * @param {boolean}  [payload.onlyMissing] Refresh every contact that is
   *                                          missing whatsapp_name or
   *                                          avatar_url (fills holes only).
   * @param {boolean}  [payload.all]      Refresh every contact (overwrites).
   * Returns { total, updated, skipped, failed, capped, results }.
   */
  async bulkRefreshContacts(payload) {
    return cmsClient.post('/api/contacts/bulk-refresh-whatsapp', payload, {
      timeout: 300000,
    });
  }

  async importContacts(formData) {
    return cmsClient.post('/api/contacts/import', formData, {
      headers: {'Content-Type': 'multipart/form-data'},
      timeout: 300000,
    });
  }

  async importContactsBulk(payload) {
    return cmsClient.post('/api/contacts/import-bulk', payload, {
      timeout: 900000,
    });
  }

  async checkPhonesBulk(phones) {
    return cmsClient.post(
      '/api/contacts/check-phones-bulk',
      {phones},
      {
        timeout: 600000,
      },
    );
  }

  async pullWAContacts(days = null, options = {}) {
    const params = {};
    if (days && days > 0) params.days = days;
    if (options.skipAvatars) params.skipAvatars = 'true';
    // 5 min: backend now also fetches avatars inline (paced) which can take
    // a while for large address books.
    return cmsClient.get('/api/contacts/wa-pull', {params, timeout: 300000});
  }

  /**
   * Batch-fetch WhatsApp profile pictures for a list of contact keys.
   * Each key is either a digits-only phone number OR a full JID like
   * "158...@lid" (required for unsaved chat partners without a phone).
   * Max 200 entries per call — chunk on the client for larger imports.
   * @param {string[]} keys
   * @param {AbortSignal} [signal]
   * @returns {Promise<{avatars: Object<string, string|null>, count: number}>}
   */
  async enrichWAAvatars(keys, signal = null) {
    return cmsClient.post(
      '/api/contacts/wa-enrich-avatars',
      {keys},
      {timeout: 180000, signal},
    );
  }

  // ==================== CONTACT GROUPS ====================

  async getContactGroups() {
    return cmsClient.get('/api/contact-groups');
  }

  async createContactGroup(data) {
    return cmsClient.post('/api/contact-groups', data);
  }

  async updateContactGroup(id, data) {
    return cmsClient.put(`/api/contact-groups/${id}`, data);
  }

  async deleteContactGroup(id, deleteContacts = false) {
    return cmsClient.delete(
      `/api/contact-groups/${id}${
        deleteContacts ? '?deleteContacts=true' : ''
      }`,
    );
  }

  async getContactGroupContacts(groupId) {
    return cmsClient.get(`/api/contact-groups/${groupId}/contacts`);
  }

  // ==================== SCHEDULED CAMPAIGNS ====================
  // Routed via /api/whatsapp/* so the WhatsApp proxy handles multipart uploads.

  async getScheduledCampaigns(params = {}) {
    return cmsClient.get('/api/whatsapp/scheduled-campaigns', {params});
  }

  async createScheduledCampaign(data) {
    const isFormData = data instanceof FormData;
    return cmsClient.post('/api/whatsapp/scheduled-campaigns', data, {
      headers: isFormData ? {'Content-Type': 'multipart/form-data'} : undefined,
    });
  }

  async updateScheduledCampaign(id, data) {
    const isFormData = data instanceof FormData;
    return cmsClient.put(`/api/whatsapp/scheduled-campaigns/${id}`, data, {
      headers: isFormData ? {'Content-Type': 'multipart/form-data'} : undefined,
    });
  }

  async deleteScheduledCampaign(id) {
    return cmsClient.delete(`/api/whatsapp/scheduled-campaigns/${id}`);
  }

  async cancelScheduledCampaign(id) {
    return cmsClient.post(`/api/whatsapp/scheduled-campaigns/${id}/cancel`);
  }

  // ==================== USER WHATSAPP GROUPS ====================

  async getUserWhatsAppGroups() {
    return cmsClient.get('/api/whatsapp-groups');
  }

  async importUserWhatsAppGroups() {
    return cmsClient.post('/api/whatsapp-groups/import');
  }

  async deleteUserWhatsAppGroup(id) {
    return cmsClient.delete(`/api/whatsapp-groups/${id}`);
  }

  // ==================== MESSAGE TEMPLATES ====================

  async getTemplates() {
    return cmsClient.get('/api/templates');
  }

  async createTemplate(data) {
    return cmsClient.post('/api/templates', data);
  }

  async updateTemplate(id, data) {
    return cmsClient.put(`/api/templates/${id}`, data);
  }

  async deleteTemplate(id) {
    return cmsClient.delete(`/api/templates/${id}`);
  }

  // ==================== CONVERSATIONS ====================

  async getConversations() {
    return cmsClient.get('/api/whatsapp/conversations');
  }

  async getChatMessages(chatId, limit = 50, before = null) {
    const params = {limit};
    if (before) params.before = before;
    return cmsClient.get(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/messages`,
      {params},
    );
  }

  async sendConversationReply(chatId, message) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/reply`,
      {message},
    );
  }

  // ============ CHAT OPERATIONS + PRESENCE (live, via cms-backend proxy) ============
  // Proxy to api-backend /api/conversations/*. JSON bodies (express.json parses
  // before the routes read req.body).

  async archiveChat(chatId, archive = true) {
    return cmsClient.put(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/archive`,
      {archive},
    );
  }

  async pinChat(chatId, pin = true) {
    return cmsClient.put(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/pin`,
      {pin},
    );
  }

  async muteChat(chatId, durationMs = null) {
    return cmsClient.put(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/mute`,
      {durationMs},
    );
  }

  async sendChatPresence(chatId, type) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/presence`,
      {type},
    );
  }

  async subscribeChatPresence(chatId) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(
        chatId,
      )}/presence/subscribe`,
    );
  }

  // ============ CHAT & MESSAGE MANAGEMENT (engine exposure, via proxy) ============

  async starMessage(chatId, messageId, star = true) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(
        chatId,
      )}/messages/${encodeURIComponent(messageId)}/star`,
      {star},
    );
  }

  async deleteMessageForMe(chatId, messageId) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(
        chatId,
      )}/messages/${encodeURIComponent(messageId)}/delete-for-me`,
    );
  }

  async fetchOlderHistory(chatId, opts = {}) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/fetch-history`,
      opts,
    );
  }

  async clearChat(chatId) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/clear`,
    );
  }

  async deleteChat(chatId) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/delete`,
    );
  }

  // ==================== AUTO-REPLY RULES ====================

  async markChatAsRead(chatId) {
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/mark-read`,
    );
  }

  async sendConversationMedia(chatId, file, caption = '') {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    return cmsClient.post(
      `/api/whatsapp/conversations/${encodeURIComponent(chatId)}/reply-media`,
      fd,
      {headers: {'Content-Type': 'multipart/form-data'}},
    );
  }

  async getAutoReplyRules() {
    return cmsClient.get('/api/whatsapp/auto-reply/rules');
  }

  async createAutoReplyRule(data) {
    return cmsClient.post('/api/whatsapp/auto-reply/rules', data);
  }

  async updateAutoReplyRule(id, data) {
    return cmsClient.put(`/api/whatsapp/auto-reply/rules/${id}`, data);
  }

  async deleteAutoReplyRule(id) {
    return cmsClient.delete(`/api/whatsapp/auto-reply/rules/${id}`);
  }

  async toggleAutoReplyRule(id) {
    return cmsClient.patch(`/api/whatsapp/auto-reply/rules/${id}/toggle`);
  }

  // ==================== SEND THROTTLE ====================

  async getAccountThrottle(accountId) {
    return cmsClient.get(`/api/whatsapp/admin/accounts/${accountId}/throttle`);
  }

  /** This number's classified disconnect history, newest first, plus a verdict. */
  /**
   * React to a message, or clear the reaction with an empty emoji.
   * Charges no message credit: a reaction is a control message, nothing new is
   * delivered (api-backend/src/routes/messages.js).
   */
  async reactToMessage(chatId, messageId, emoji) {
    return cmsClient.post('/api/whatsapp/messages/react', {
      chatId,
      messageId,
      emoji,
    });
  }

  /** Rewrite a message already sent. Also free. */
  async editSentMessage(chatId, messageId, text) {
    return cmsClient.post('/api/whatsapp/messages/edit', {
      chatId,
      messageId,
      text,
    });
  }

  /** Unsend: revoke the message for everyone, not just this device. */
  async deleteMessageForEveryone(chatId, messageId) {
    return cmsClient.post('/api/whatsapp/messages/delete', {chatId, messageId});
  }

  async getAccountRestrictions(accountId, limit) {
    return cmsClient.get(`/api/whatsapp/accounts/${accountId}/restrictions`, {
      params: limit ? {limit} : undefined,
    });
  }

  async getAccountAutoRead(accountId) {
    return cmsClient.get(`/api/whatsapp/accounts/${accountId}/auto-read`);
  }

  /**
   * Mark a chat read once the platform has ANSWERED it with an auto-reply.
   * Never on arrival — that would clear the unread badge on a handset a person
   * may be holding.
   */
  async setAccountAutoRead(accountId, enabled) {
    return cmsClient.put(`/api/whatsapp/accounts/${accountId}/auto-read`, {
      enabled,
    });
  }

  async updateAccountThrottle(accountId, data) {
    return cmsClient.patch(
      `/api/whatsapp/admin/accounts/${accountId}/throttle`,
      data,
    );
  }

  async getMyThrottleStatus() {
    return cmsClient.get('/api/whatsapp/admin/me/throttle');
  }

  // ==================== ADMIN ACCOUNTS (with throttle data) ====================

  async getAdminAccounts(params = {}) {
    return cmsClient.get('/api/whatsapp/admin/accounts', {params});
  }

  // ==================== THROTTLE MONITORING (admin only) ====================

  async getThrottleHealth() {
    return cmsClient.get('/api/whatsapp/admin/throttle/health');
  }

  async getThrottleCircuits() {
    return cmsClient.get('/api/whatsapp/admin/throttle/circuits');
  }

  async closeThrottleCircuit(accountId) {
    return cmsClient.post(
      `/api/whatsapp/admin/throttle/circuits/${accountId}/close`,
    );
  }

  async getThrottleQueueDepth() {
    return cmsClient.get('/api/whatsapp/admin/throttle/queue-depth');
  }

  async getThrottleAccountDepth(accountId) {
    return cmsClient.get(
      `/api/whatsapp/admin/throttle/queue-depth/${accountId}`,
    );
  }

  // ==================== THROTTLE METRICS & REBALANCE (v4.0) ====================

  async getThrottleMetrics() {
    return cmsClient.get('/api/whatsapp/admin/throttle/metrics');
  }

  async rebalanceWorkers(targetWorkers) {
    return cmsClient.post('/api/whatsapp/admin/throttle/rebalance', {
      target_workers: targetWorkers,
    });
  }

  // ==================== ADMIN SEND ON BEHALF ====================

  async sendOnBehalf(targetUserId, to, message) {
    return cmsClient.post('/api/whatsapp/admin/send-on-behalf', {
      targetUserId,
      to,
      message,
    });
  }

  // ==================== SYSTEM MONITORING (v4.1) ====================

  async getQueueStatus(signal = null) {
    return cmsClient.get('/api/monitoring/queues', {signal});
  }

  async getUserAnalytics(params = {}, signal = null) {
    return cmsClient.get('/api/monitoring/analytics', {params, signal});
  }

  async getMessageFlow(signal = null) {
    return cmsClient.get('/api/monitoring/flow', {signal});
  }

  async getUserQueueStatus(signal = null) {
    return cmsClient.get('/api/monitoring/users', {signal});
  }

  async getScalingMetrics(signal = null) {
    return cmsClient.get('/api/monitoring/scaling', {signal});
  }

  async getAllMonitoring(params = {}, signal = null) {
    return cmsClient.get('/api/monitoring/all', {params, signal});
  }

  async pauseSend() {
    return cmsClient.post('/api/monitoring/pause-send');
  }

  async resumeSend() {
    return cmsClient.post('/api/monitoring/resume-send');
  }

  async getSendPauseState(signal = null) {
    return cmsClient.get('/api/monitoring/send-pause-state', {signal});
  }

  async clearQueue(allUsers = false) {
    const url = allUsers
      ? '/api/monitoring/clear-queue?all=true'
      : '/api/monitoring/clear-queue';
    return cmsClient.post(url);
  }

  async systemPause() {
    return cmsClient.post('/api/monitoring/system-pause');
  }

  async systemDrain() {
    return cmsClient.post('/api/monitoring/system-drain');
  }

  async systemResume() {
    return cmsClient.post('/api/monitoring/system-resume');
  }

  async getCircuits(signal = null) {
    return cmsClient.get('/api/monitoring/circuits', {signal});
  }

  async closeCircuit(accountId) {
    return cmsClient.post(`/api/monitoring/circuits/${accountId}/close`);
  }
}

const unifiedApi = new UnifiedApiService();
export default unifiedApi;
